import { useCallback } from 'react';
import { useAppSelector } from '../store/hooks';

const READ_ACTIONS = ['view', 'export'];

// Actions the plan sells as their own feature key
const ACTION_FEATURES: Record<string, string> = { 'expenses:approve': 'expenses.approve' };

// Create actions that count against a plan limit
const LIMIT_FOR: Record<string, { key: string; label: string }> = {
    'inventory.items': { key: 'items', label: 'items' },
    'inventory.locations': { key: 'locations', label: 'locations' },
    'employee.employees': { key: 'employees', label: 'employees' },
    'sales.gst_invoice': { key: 'invoicesPerMonth', label: 'invoices per month' },
    'sales.non_gst_invoice': { key: 'invoicesPerMonth', label: 'invoices per month' },
    members: { key: 'users', label: 'users' },
    ocr: { key: 'ocrPerMonth', label: 'OCR scans per month' },
};

/**
 * Gating for the LOGGED-IN user, against their own module grants.
 *
 * Mirrors the backend resolver (ibook-backend/src/middleware/moduleAccess.js →
 * userCan) exactly, so the UI never offers an action the API would reject and
 * never blocks one it would allow:
 *
 *   - subscription plan first (applies to the owner too; see planAccess.js)
 *   - grants not loaded yet           => everything allowed (the API still guards)
 *   - account owner                    => everything allowed
 *   - a module-level grant covers ALL of that module's sub-modules
 *   - otherwise a matching sub-module grant is required
 *
 * The grants themselves come from state.auth.modulePermissions, which the
 * loadUserPermissions thunk fills from GET /permissions/user/:id. They already
 * include the defaults the user's role provides. The plan comes from
 * state.auth.plan (loadPlan thunk, GET /subscription/current).
 *
 * Note this is NOT the same as useModulePermissions — that hook is the admin
 * CRUD wrapper for editing ANOTHER user's permissions and issues requests on
 * mount. This one is read-only redux state and safe inside grid cells.
 */
export const useModuleAccess = () => {
    const { modulePermissions, plan } = useAppSelector((state) => state.auth);

    // Always enforced once the user's grants have loaded.
    const enforceModuleAccess = !!modulePermissions;
    // Admin-level access is deliberately folded into the owner check: a user an
    // Admin has flagged with full access is unrestricted, exactly like the owner.
    const isAccountOwner = !!(modulePermissions?.isAccountOwner || modulePermissions?.hasFullAccess);

    /** Why the plan blocks this action, or null when it is allowed. */
    const planDenial = useCallback(
        (moduleKey: string, action: string, subModuleKey?: string | null): string | null => {
            if (!plan?.enforced) return null;
            if (plan.blocked) return 'Your subscription has ended. Renew to continue.';
            const key = ACTION_FEATURES[`${moduleKey}:${action}`] || (subModuleKey ? `${moduleKey}.${subModuleKey}` : moduleKey);
            const inPlan = (plan.modules || []).includes(key);
            const isRead = READ_ACTIONS.includes(action);
            if (!inPlan) return 'Not included in your plan. Upgrade to use it.';
            if (!isRead && (plan.viewOnlyModules || []).includes(key)) return 'Your plan can view this but not change it. Upgrade to make changes.';
            const limit = action === 'create' ? LIMIT_FOR[key] : null;
            const usage = limit && plan.usage?.[limit.key];
            if (usage && usage.limit >= 0 && usage.used >= usage.limit) {
                return `Your plan allows ${usage.limit} ${limit!.label}. Upgrade to add more.`;
            }
            return null;
        },
        [plan]
    );

    /** Does the plan include any part of this module? */
    const planAllowsModule = useCallback(
        (moduleKey: string): boolean => {
            if (!plan?.enforced) return true;
            if (plan.blocked) return false;
            return (plan.modules || []).some((k: string) => k === moduleKey || k.startsWith(`${moduleKey}.`));
        },
        [plan]
    );

    /** Can the user perform `action` on this module (or one of its sub-modules)? */
    const can = useCallback(
        (moduleKey: string, action: string, subModuleKey?: string | null): boolean => {
            if (planDenial(moduleKey, action, subModuleKey)) return false;
            if (!enforceModuleAccess || isAccountOwner) return true;
            const ma = modulePermissions?.moduleAccess?.[moduleKey];
            if (!ma) return false;
            if (Array.isArray(ma.permissions) && ma.permissions.includes(action)) return true;
            if (subModuleKey && ma.subModules && ma.subModules[subModuleKey]) {
                const sub = ma.subModules[subModuleKey];
                return Array.isArray(sub.permissions) && sub.permissions.includes(action);
            }
            return false;
        },
        [enforceModuleAccess, isAccountOwner, modulePermissions, planDenial]
    );

    /** Does the user have ANY access to the module? (sidebar-style visibility) */
    const canAccessModule = useCallback(
        (moduleKey: string): boolean => {
            if (!planAllowsModule(moduleKey)) return false;
            if (!enforceModuleAccess || isAccountOwner) return true;
            const ma = modulePermissions?.moduleAccess?.[moduleKey];
            if (!ma) return false;
            const hasModuleLevel = Array.isArray(ma.permissions) && ma.permissions.length > 0;
            const hasSubModule = !!(ma.subModules && Object.keys(ma.subModules).length > 0);
            return hasModuleLevel || hasSubModule;
        },
        [enforceModuleAccess, isAccountOwner, modulePermissions, planAllowsModule]
    );

    /** Tooltip text for a locked action: the plan reason, else the permission one. */
    const denyReason = useCallback(
        (moduleKey: string, action: string, subModuleKey?: string | null): string =>
            planDenial(moduleKey, action, subModuleKey) || `You do not have permission to ${action} in ${moduleKey}`,
        [planDenial]
    );

    return { can, canAccessModule, planAllowsModule, planDenial, denyReason, enforceModuleAccess, isAccountOwner };
};

export default useModuleAccess;
